import { z } from "zod";
import { schemas } from "@/api";

export const FEEDBACK_CATEGORIES = ["bug", "feature"] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

// Mirrors _MAX_SCREENSHOT_SIZE in backend/src/api/routes/feedback.py (the server silently drops
// bigger files, so the client refuses them up front). The server has no count cap; 5 is the old UI's.
export const MAX_SCREENSHOTS = 5;
export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
// page_url / user_agent columns (Feedback model); the server truncates too.
export const MAX_CONTEXT_LENGTH = 500;

// Rules come from the generated body schema (the backend's Form constraints).
const body = schemas.zBodySubmitFeedbackApiV1FeedbackPost.shape;
export const TITLE_MAX_LENGTH = body.title.maxLength ?? 255;
export const DESCRIPTION_MAX_LENGTH = body.description.maxLength ?? 10_000;

export interface FeedbackMessages {
  titleRequired: string;
  titleTooLong: string;
  descriptionRequired: string;
  descriptionTooLong: string;
}

/** Required text: not blank after trimming, then the generated rule (translated messages come first). */
function requiredText(generated: z.ZodString, max: number, required: string, tooLong: string) {
  return z.string().trim().min(1, required).max(max, tooLong).pipe(generated);
}

export function feedbackSchema(m: FeedbackMessages) {
  return z.object({
    category: body.category,
    title: requiredText(body.title, TITLE_MAX_LENGTH, m.titleRequired, m.titleTooLong),
    description: requiredText(body.description, DESCRIPTION_MAX_LENGTH, m.descriptionRequired, m.descriptionTooLong),
    screenshots: z.array(z.object({ id: z.number(), file: z.instanceof(File) })).max(MAX_SCREENSHOTS),
  });
}

export type ScreenshotRejection = "type" | "size" | "count";

/** One of my screenshots. The same file can be added twice, so `id` tells the copies apart. */
export interface Screenshot {
  id: number;
  file: File;
}

let lastScreenshotId = 0;

/**
 * Add `incoming` to `existing`: images up to MAX_SCREENSHOT_BYTES each, `max` in all (fewer than
 * MAX_SCREENSHOTS while the page screenshot takes a place). Each added file gets a new id.
 * `rejected` names why a file was refused (the first reason met), or null when all were added.
 */
export function acceptScreenshots(
  existing: readonly Screenshot[],
  incoming: readonly File[],
  max = MAX_SCREENSHOTS,
): { screenshots: Screenshot[]; rejected: ScreenshotRejection | null } {
  const screenshots = [...existing];
  let rejected: ScreenshotRejection | null = null;
  for (const file of incoming) {
    if (!file.type.startsWith("image/")) rejected ??= "type";
    else if (file.size > MAX_SCREENSHOT_BYTES) rejected ??= "size";
    else if (screenshots.length >= max) rejected ??= "count";
    else screenshots.push({ id: ++lastScreenshotId, file });
  }
  return { screenshots, rejected };
}

export const truncateContext = (value: string) => value.slice(0, MAX_CONTEXT_LENGTH);
